/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { ApprovalRounded, Edit } from '@mui/icons-material';
import { CircularProgress, IconButton, MenuItem, Select, Tooltip } from '@mui/material';
import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';
import useForm from '../../common/hooks/useForm';
import type { QidType } from '../../types/QidType';

import {
  epochToDate,
  convertDateInputTo8AMUTC,
  addMonthsYrsToTimestamp,
  formatDateYYYYMMDD,
} from '../../common/UtilityFunctions';

type AddSubsidyFormProps = SubsidyObj & {
  subsidyNameQidList;
  createLoanSubsidy: (
    fields: Record<string, any>,
    setErrors: Dispatch<SetStateAction<Record<string, any>>>,
  ) => Promise<void>;
};

function AddSubsidyForm({
  subsidyName,
  startDate,
  endDate,
  approvedAmount,
  agencyCureAmount,
  totalAgencySubsidyAmount,
  subsidyNameQidList,
  createLoanSubsidy,
}: AddSubsidyFormProps) {
  const [isAdding, setIsAdding] = useState(false);
  const ref = useRef();
  const handleDateChange = useCallback((name, value, prev) => {
    if (
      (name === 'startDate' && prev.subsidyName === 'MOPR' && value) ||
      (name === 'subsidyName' && value === 'MOPR' && prev.startDate)
    ) {
      const startTs = convertDateInputTo8AMUTC(name === 'startDate' ? value : prev.startDate).getTime() / 1000;
      const endTs = addMonthsYrsToTimestamp(startTs, 11, 2); // + 11 months, 2 years
      return {
        ...prev,
        [name]: value,
        endDate: formatDateYYYYMMDD(new Date(endTs * 1000)),
      };
    }
    return {
      ...prev,
      [name]: value,
    };
  }, []);
  const { fields, setFields, errors, isSubmitting, handleChange, handleSubmit } = useForm(
    {
      subsidyName,
      startDate,
      endDate,
      approvedAmount,
      agencyCureAmount,
      totalAgencySubsidyAmount,
    },
    createLoanSubsidy,
    handleDateChange,
  );

  const toggleIsAdding = useCallback(() => {
    const fields = {
      subsidyName,
      startDate: startDate ? epochToDate(startDate).toISOString().split('T')[0] : '',
      endDate: endDate ? epochToDate(endDate).toISOString().split('T')[0] : '',
      approvedAmount,
      agencyCureAmount,
      totalAgencySubsidyAmount,
    };

    // const editObj: SubsidyObj = useLoanSubsidy(cnt);
    console.log(fields);
    // console.log(editObj);
    setFields(fields);
    setIsAdding((prev) => !prev);
  }, [subsidyName, startDate, endDate, approvedAmount, agencyCureAmount, totalAgencySubsidyAmount, setFields]);

  return isAdding ? (
    <td colSpan={8}>
      <table>
        <tbody ref={ref}>
          <tr>
            <td>Subsidy Name</td>
            <td>
              <Select name='subsidyName' value={fields.subsidyName} onChange={handleChange} displayEmpty>
                <MenuItem key={'blank'} value={''}>
                  {'Select Subsidy Name'}
                </MenuItem>
                {subsidyNameQidList.map((row: QidType, index: number) => (
                  <MenuItem key={index} value={row.code}>
                    {row.code} - {row.description}
                  </MenuItem>
                ))}
              </Select>
              {errors.subsidyName && <small>{errors.subsidyName}</small>}
            </td>
          </tr>
          <tr>
            <td>Starting Due Date (1st of month)</td>
            <td>
              <input type='date' name='startDate' value={fields.startDate} onChange={handleChange} />
              {errors.startDate && <small>{errors.startDate}</small>}
            </td>
          </tr>
          <tr>
            <td>Last Due Date (1st of month)</td>
            <td>
              <input type='date' name='endDate' value={fields.endDate} onChange={handleChange} />
              {errors.endDate && <small>{errors.endDate}</small>}
            </td>
          </tr>
          <tr>
            <td>Approved Amount</td>
            <td>
              <input type='number' name='approvedAmount' value={fields.approvedAmount} onChange={handleChange} />
              {errors.approvedAmount && <small>{errors.approvedAmount}</small>}
            </td>
          </tr>
          <tr>
            <td>Amount Used to Cure Delinquency</td>
            <td>
              <input type='number' name='agencyCureAmount' value={fields.agencyCureAmount} onChange={handleChange} />
              {errors.agencyCureAmount && <small>{errors.agencyCureAmount}</small>}
            </td>
          </tr>
          <tr>
            <td>Total Amount</td>
            <td>
              <input
                type='number'
                name='totalAgencySubsidyAmount'
                value={fields.totalAgencySubsidyAmount}
                onChange={handleChange}
              />
              {errors.totalAgencySubsidyAmount && <small>{errors.totalAgencySubsidyAmount}</small>}
            </td>
          </tr>
          <tr>
            <td>
              {isSubmitting ? (
                <CircularProgress size='1.5rem' />
              ) : (
                <Tooltip title='Add'>
                  <IconButton
                    onClick={(e) => {
                      handleSubmit(e);
                      toggleIsAdding();
                    }}
                  >
                    <ApprovalRounded />
                    Add
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title='Cancel'>
                <IconButton onClick={toggleIsAdding}>
                  <ApprovalRounded />
                  Cancel
                </IconButton>
              </Tooltip>
            </td>
            <td colSpan={3}>
              <p>* Indicates required field</p>
              {errors.generic && <small>{errors.generic}</small>}
            </td>
          </tr>
        </tbody>
      </table>
    </td>
  ) : (
    <td>
      <Tooltip title='Add'>
        <IconButton onClick={toggleIsAdding}>
          <Edit />
        </IconButton>
      </Tooltip>
      Add
    </td>
  );
}

export default AddSubsidyForm;
